/**
 * HVP Cascade Controller
 *
 * Modern ES6+ variation controller with zero jQuery dependency
 * Handles server-side cascading, state management, and UI updates
 *
 * @package HaruVariationsPro
 * @version 1.0.0
 */

import { HVPApiClient } from './api-client.js';
import { HVPCacheManager } from './cache-manager.js';
import { HVPEventBus } from './event-bus.js';
import { HVPClientCascade } from './client-cascade.js';
import { HVPDomPatcher } from './dom-patcher.js';
import { HVPUpdateScheduler } from './update-scheduler.js';
import { HVPUIManager } from './ui-manager.js';
import { decodePackedVariations } from './variation-packer.js';

import { applyMixin } from './mixin.js';
import { CascadeLifecycleMethods } from './cascade-lifecycle.js';
import { CascadeChangeMethods } from './cascade-change.js';
import { CascadeFocusMethods } from './cascade-focus.js';
import { CascadeCompatMethods } from './cascade-compat.js';
import { CascadeOptionsMethods } from './cascade-options.js';
import { CascadeDropdownMethods } from './cascade-dropdowns.js';
import { CascadeVariationMethods } from './cascade-variation.js';
import { CascadeApplyMethods } from './cascade-apply.js';
import { CascadeRestoreMethods } from './cascade-restore.js';
import { CascadeWcOverrideMethods } from './cascade-wc-override.js';
import { CascadeEventMethods } from './cascade-events.js';
import { CascadeStateMethods } from './cascade-state.js';
import { CascadeHealthMethods } from './cascade-health.js';

export class HVPCascadeController {
    /**
     * Constructor
     *
     * @param {Object} config - Configuration object
     */
    constructor(config = {}) {
        // Configuration
        this.config = config;
        this.productId = config.productId || 0;
        this.variationCount = Number(config.variationCount) || 0;
        this.minVariations = Number(config.minVariations) || 30;
        this.attributeOrder = config.attributeOrder || [];
        this.prefetchedOptions = config.prefetchedOptions || {};
        this.debug = config.debug || false;

        // Initialize components
        this.api = new HVPApiClient({
            ajaxUrl: config.ajaxUrl,
            nonce: config.nonce,
            debug: this.debug
        });

        this.cache = new HVPCacheManager({
            maxSize: 100,
            defaultTtl: 300,
            debug: this.debug,
            sessionStorage: true,
            productId: this.productId
        });

        this.eventBus = new HVPEventBus();

        // DOM Patcher for flickering-free updates
        // Pass term labels for proper display names (e.g., "4/0" instead of "4-0")
        this.domPatcher = new HVPDomPatcher({
            debug: this.debug,
            termLabels: this.config.termLabels || {}
        });

        // Update Scheduler for batched, two-phase updates
        this.scheduler = new HVPUpdateScheduler({
            debug: this.debug
        });

        // UI Manager for highlighting and progress
        this.uiManager = new HVPUIManager({
            debug: this.debug,
            scheduler: this.scheduler
        });

        // Health check interval
        this.healthCheckInterval = null;

        // WooCommerce override tracking
        this.wcOverrideApplied = false;

        // State
        this.selections = {};
        this.currentVariation = null;
        this.isLoading = false;
        this.initialized = false;

        // DOM references
        this.form = null;
        this.dropdowns = new Map();

        // UI state
        this.animationsEnabled = config.animationsEnabled !== false;

        // Bind methods
        this.handleDropdownChange = this.handleDropdownChange.bind(this);
        this.handleDropdownFocus = this.handleDropdownFocus.bind(this);
        this.handleReset = this.handleReset.bind(this);
        this.handlePageShow = this.handlePageShow.bind(this);

        // Back/forward-navigation restore reconcile state
        this.isReconcilingRestore = false;
        this.restoreReconcileTimers = [];

        // Smart Cascade state
        this.smartCascadeEnabled = config.smartCascadeEnabled !== false;
        this.focusLoadingAttribute = null;
        this.focusRequestCounter = 0; // Counter for race condition prevention

        // Client-side cascade (for instant performance with preloaded data).
        // Prefer the compact `variationsPacked` payload, decoded back to the
        // identical full variation array; fall back to legacy `allVariations`.
        this.allVariations = this.resolveVariations(config);
        this.cascadeMode = config.cascadeMode || 'server-side';
        this.clientCascade = null;

        // Initialize client cascade if data available
        if (this.allVariations.length > 0) {
            this.clientCascade = new HVPClientCascade(
                this.allVariations,
                this.attributeOrder,
                this.debug
            );
            window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Client-side cascade engine initialized with', this.allVariations.length, 'variations');
        }
    }

    /**
     * Resolve the variation dataset for the client cascade.
     *
     * Prefers the compact `variationsPacked` payload, decoded back to the
     * identical full [{variation_id, attributes, ...}] array (kept lossless by a
     * PHP self-check that ships the full array on any mismatch). Falls back to
     * the legacy `allVariations` array for back-compat / when unpacked.
     *
     * @param {Object} config - hvpData configuration.
     * @returns {Array} Full variation objects.
     */
    resolveVariations(config) {
        if (config && config.variationsPacked && Array.isArray(config.variationsPacked.rows)) {
            try {
                const decoded = decodePackedVariations(config.variationsPacked);

                if (config.debug) {
                    const expected = config.variationsPacked.rows.length;
                    const countOk = decoded.length === expected;
                    console.log(
                        '[HVP DEBUG] Decoded ' + decoded.length + ' variations from variationsPacked' +
                        ' (rows=' + expected + ', priceMode=' + config.variationsPacked.priceMode + ')' +
                        (countOk ? '' : ' ⚠ COUNT MISMATCH')
                    );
                    const vc = Number(config.variationCount) || 0;
                    if (vc && vc !== decoded.length) {
                        console.warn(
                            '[HVP DEBUG] decoded.length (' + decoded.length + ') != variationCount (' + vc +
                            ') — DB count and cached dataset can legitimately differ'
                        );
                    }
                    if (decoded.length) {
                        console.log('[HVP DEBUG] Sample decoded variation:', decoded[0]);
                    }
                }

                return decoded;
            } catch (e) {
                console.error('[HVP] Failed to decode variationsPacked, falling back to allVariations:', e);
            }
        }

        return (config && config.allVariations) || [];
    }
}

// Method groups live in sibling modules (350-line rule) and are installed on
// the prototype here, at module-evaluation time — before main.js can construct
// an instance — with the same property descriptors class methods get.
applyMixin(HVPCascadeController.prototype, CascadeLifecycleMethods);
applyMixin(HVPCascadeController.prototype, CascadeChangeMethods);
applyMixin(HVPCascadeController.prototype, CascadeFocusMethods);
applyMixin(HVPCascadeController.prototype, CascadeCompatMethods);
applyMixin(HVPCascadeController.prototype, CascadeOptionsMethods);
applyMixin(HVPCascadeController.prototype, CascadeDropdownMethods);
applyMixin(HVPCascadeController.prototype, CascadeVariationMethods);
applyMixin(HVPCascadeController.prototype, CascadeApplyMethods);
applyMixin(HVPCascadeController.prototype, CascadeRestoreMethods);
applyMixin(HVPCascadeController.prototype, CascadeWcOverrideMethods);
applyMixin(HVPCascadeController.prototype, CascadeEventMethods);
applyMixin(HVPCascadeController.prototype, CascadeStateMethods);
applyMixin(HVPCascadeController.prototype, CascadeHealthMethods);

export default HVPCascadeController;
